import {
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import {
  executeRecesion,
  getAECsInPosession,
} from '../../../../../../../lib/api/reSell/factoring';
import {
  createDocumentsForCession,
  getCessionsToCreateNominee,
} from '../../../../../../../lib/api/reverseCession';
import GlobalSearcher, {
  Filter,
} from '../../../../../../../lib/common/GlobalSearcher';
import RutSearcher from '../../../../../../../lib/common/RutSearcher';
import CustomStepper, {
  StepData,
} from '../../../../../../../lib/common/Stepper';
import CustomButton from '../../../../../../../lib/designSystem/Button';
import CenteredCircularProgress from '../../../../../../../lib/designSystem/CircularProgress';
import EnhancedTable from '../../../../../../../lib/designSystem/Table';
import TableWithPagination from '../../../../../../../lib/designSystem/TableWithPagination';
import { showAlert } from '../../../../../../../lib/functions/utils';

interface Business {
  id?: string;
  identifier: string;
}

interface CessionRequest {
  id: string;
  folio: string;
  businessIdentifier: string;
  stakeholderIdentifier: string;
  amount: number;
  subType: string;
  issuedDate: string;
  cessionDate: Date;
  status?: boolean;
  message?: string;
}

enum CessionType {
  REVERSE = 'REVERSE',
  EXTERNAL = 'EXTERNAL',
}

interface CessionDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  onSuccess?: () => Promise<void>;
}

const CessionDialog: React.FC<CessionDialogProps> = (props) => {
  const { open, setOpen, onSuccess } = props;
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [documentsWithStatus, setDocumentsWithStatus] = useState<
    CessionRequest[]
  >([]);
  const [documentsResult, setDocumentsResult] = useState<CessionRequest[]>([]);
  const [selectedBusiness, setSelectedBusiness] = useState<Business | null>(
    null,
  );
  const [businessSearched, setBusinessSearched] = useState(false);
  const [cessionType, setCessionType] = useState<CessionType>(
    CessionType.REVERSE,
  );
  const [activeStep, setActiveStep] = useState(0);
  const [stepperCompleted, setStepperCompleted] = useState(false);

  const [cessions, setCessions] = useState([]);
  const [selectedCessions, setSelectedCessions] = useState<string[]>([]);
  const [selectedCessionsMap, setSelectedCessionsMap] = useState<
    Map<string, any>
  >(new Map());
  const [cessionsLoading, setCessionsLoading] = useState(false);
  const [firstStepLoading, setFirstStepLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const startDateDefault = new Date();
  startDateDefault.setMonth(startDateDefault.getMonth() - 1);
  const [startDate, setStartDate] = useState(dayjs(startDateDefault));
  const [endDate, setEndDate] = useState(dayjs(new Date()));
  const [businessIdentifier, setBusinessIdentifier] = useState('');
  const [stakeholderIdentifier, setStakeholderIdentifier] = useState('');
  const [folio, setFolio] = useState('');
  const [cessionaryIdentifier, setCessionaryIdentifier] = useState('');
  const PAGE_SIZE = 5;
  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: PAGE_SIZE,
    total: 0,
    totalPages: 0,
  });

  const headers: { key: string; label: string; type?: any }[] = [
    { key: 'folio', label: 'Folio' },
    { key: 'docType', label: 'Tipo' },
    { key: 'businessName', label: 'Cliente' },
    { key: 'businessIdentifier', label: 'RUT' },
    { key: 'amount', label: 'Monto Documento', type: 'money' },
    { key: 'issuedDate', label: 'Emisión', type: 'date' },
    { key: 'cessionDate', label: 'Cesion', type: 'date' },
  ];

  const generateQuery = ({
    page,
    pageSize,
  }: {
    page: number;
    pageSize: number;
  }) => {
    let query = '';
    if (startDate) query += `startDate=${startDate}&`;
    if (endDate) query += `endDate=${endDate}&`;
    if (businessIdentifier)
      query += `businessIdentifier=${businessIdentifier}&`;
    if (stakeholderIdentifier)
      query += `stakeholderIdentifier=${stakeholderIdentifier}&`;
    if (cessionaryIdentifier)
      query += `cessionaryIdentifier=${cessionaryIdentifier}&`;
    if (folio) query += `folio=${folio}&`;
    if (page) query += `page=${page}&pageSize=${pageSize}`;
    return query;
  };

  const fetchCessionsToCreateNominee = async ({
    page,
    pageSize,
  }: {
    page: number;
    pageSize: number;
  }) => {
    try {
      setCessionsLoading(true);
      const query = generateQuery({
        page: page,
        pageSize: pageSize,
      });
      const response = await getCessionsToCreateNominee(dispatch, query);
      setCessions(response.data);
      setPagination(response.pagination);
    } catch (error) {
      console.error(error);
    } finally {
      setCessionsLoading(false);
      setInitialLoading(false);
    }
  };

  const filters: Filter[] = [
    {
      type: 'date',
      label: 'Fecha cesion inicio',
      value: startDate,
      setter: setStartDate,
      dateKey: 'startDate',
    },
    {
      type: 'date',
      label: 'Fecha cesion fin',
      value: endDate,
      setter: setEndDate,
      dateKey: 'endDate',
    },
    {
      type: 'text',
      label: 'Folio',
      value: folio,
      setter: setFolio,
    },
    {
      type: 'text',
      label: 'RUT emisor',
      value: businessIdentifier,
      setter: setBusinessIdentifier,
    },
    {
      type: 'text',
      label: 'RUT deudor',
      value: stakeholderIdentifier,
      setter: setStakeholderIdentifier,
    },
    {
      type: 'text',
      label: 'RUT cesionario',
      value: cessionaryIdentifier,
      setter: setCessionaryIdentifier,
    },
  ];

  const handleCessionsSelected = async (selectedCessionsData: any[]) => {
    try {
      setFirstStepLoading(true);
      const response = await createDocumentsForCession(dispatch, {
        documents: selectedCessionsData,
      });

      const responseData = Array.isArray(response)
        ? response
        : response?.data || response;

      const processedDocuments: CessionRequest[] = responseData.map(
        (doc: any) => ({
          id: doc.id || doc.documentId,
          folio: doc.folio,
          businessIdentifier: doc.businessIdentifier,
          businessName: doc.businessName,
          stakeholderIdentifier: doc.stakeholderIdentifier,
          amount: doc.amount,
          subType: doc.subType,
          issuedDate: doc.issuedDate,
        }),
      );

      const documents = processedDocuments.map((doc) => ({
        id: doc.id,
        folio: doc.folio,
        businessIdentifier: doc.businessIdentifier,
        cessionDate: doc.cessionDate,
      }));
      const possessionResponse = await getAECsInPosession(dispatch, documents);

      const documentsWithStatus = processedDocuments.map(
        (document: CessionRequest) => {
          const documentWithStatus = possessionResponse.find(
            (item: any) => item.id === document.id,
          );
          return {
            ...document,
            status: documentWithStatus?.status ?? false,
            message: documentWithStatus?.message ?? 'Documento no encontrado',
          };
        },
      );

      setDocumentsWithStatus(documentsWithStatus);
    } catch (error) {
      showAlert(
        dispatch,
        'error',
        'Error al procesar las cesiones seleccionadas',
      );
      console.error(error);
      throw error;
    } finally {
      setFirstStepLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const payload = {
        type: cessionType,
        documents: documentsWithStatus.map((document: CessionRequest) => ({
          id: document.id,
          folio: document.folio,
          businessIdentifier: document.businessIdentifier,
          stakeholderIdentifier: document.stakeholderIdentifier,
          amount: document.amount,
          subType: document.subType,
          ...(cessionType === CessionType.EXTERNAL &&
            selectedBusiness && {
              externalCompanyIdentifier: selectedBusiness.identifier,
            }),
          cessionDate: new Date(),
        })),
      };

      const response: any = await executeRecesion(dispatch, payload);

      const mergedDocuments = documentsWithStatus.map(
        (document: CessionRequest) => {
          const responseForDocument = response.results.find(
            (item: any) => item.id === document.id,
          );
          return {
            ...document,
            status: responseForDocument?.status,
            message: responseForDocument?.message,
          };
        },
      );
      setDocumentsResult(mergedDocuments);
      const documentsReselled = mergedDocuments.filter(
        (document: CessionRequest) => document.status,
      );
      if (documentsReselled.length > 0) {
        const actionText =
          cessionType === CessionType.REVERSE ? 'revertidas' : 'cedidas';
        const text = `Cesiones ${actionText} correctamente para ${documentsReselled.length} facturas. La cesión demora entre 2 a 10 minutos en ser reflejada en el SII.`;
        showAlert(dispatch, 'success', text);
        if (onSuccess) {
          await onSuccess();
        }
        setOpen(false);
      } else {
        showAlert(
          dispatch,
          'error',
          'Error al generar algunas cesiones, por favor verifique los datos',
        );
      }
    } catch (error) {
      showAlert(dispatch, 'error', 'Error al generar cesiones');
    } finally {
      setLoading(false);
    }
  };

  const canMakeCession =
    documentsWithStatus.length > 0 &&
    documentsWithStatus.every((document: CessionRequest) => document.status) &&
    (cessionType === CessionType.REVERSE ||
      (cessionType === CessionType.EXTERNAL && selectedBusiness));

  useEffect(() => {
    if (cessionType === CessionType.REVERSE) {
      setSelectedBusiness(null);
      setBusinessSearched(false);
    }
  }, [cessionType]);

  const steps: StepData[] = [
    {
      label: 'Seleccionar cesiones',
      description: 'Selecciona las cesiones que deseas procesar',
      content: (
        <div>
          <Typography
            variant="body1"
            style={{ marginBottom: '15px' }}
          ></Typography>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '6px 0px',
              marginBottom: '16px',
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Usa los filtros para buscar cesiones específicas
            </Typography>
            <GlobalSearcher
              filters={filters}
              triggerSearch={() =>
                fetchCessionsToCreateNominee({ page: 1, pageSize: 10 })
              }
            />
          </div>
          <TableWithPagination
            dataHeaders={headers}
            data={cessions}
            setData={setCessions}
            pagination={pagination}
            setPagination={setPagination}
            searchData={fetchCessionsToCreateNominee}
            selected={selectedCessions}
            setSelected={setSelectedCessions}
          />
        </div>
      ),
      async: true,
      onAsyncStep: async () => {
        const selectedCessionsData = Array.from(selectedCessionsMap.values());
        await handleCessionsSelected(selectedCessionsData);
      },
      disabled: selectedCessions.length === 0,
    },
    {
      label: 'Revisar facturas',
      description: 'Revisa las facturas que van a ser cedidas',
      content: (
        <div>
          <Typography variant="body1" style={{ marginBottom: '15px' }}>
            A continuación se muestran todas las facturas que van a ser cedidas:
          </Typography>
          {documentsWithStatus.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No hay facturas para mostrar. Asegúrate de haber seleccionado
              cesiones en el paso anterior.
            </Typography>
          ) : (
            <>
              {documentsWithStatus.some(
                (doc: CessionRequest) => !doc.status,
              ) && (
                <Alert severity="error" style={{ marginBottom: '15px' }}>
                  Algunas facturas no están disponibles para cesión.
                </Alert>
              )}
              <EnhancedTable
                data={documentsWithStatus}
                dataHeaders={[
                  {
                    key: 'businessName',
                    label: 'Razón social',
                  },
                  {
                    key: 'businessIdentifier',
                    label: 'RUT',
                  },
                  {
                    key: 'folio',
                    label: 'Folio',
                  },
                  {
                    key: 'amount',
                    label: 'Monto',
                    type: 'money',
                  },
                  {
                    key: 'issuedDate',
                    label: 'Fecha emisión',
                    type: 'date',
                  },
                  {
                    key: 'status',
                    label: 'Estado',
                    type: 'yes-no-circle',
                  },
                  {
                    key: 'message',
                    label: 'Mensaje',
                  },
                ]}
              />
            </>
          )}
        </div>
      ),
      disabled:
        documentsWithStatus.length === 0 ||
        documentsWithStatus.some((doc: CessionRequest) => !doc.status),
    },
    {
      label: 'Tipo de cesión',
      description: 'Selecciona el tipo de cesión que deseas realizar',
      content: (
        <div>
          <Typography variant="body1" style={{ marginBottom: '15px' }}>
            Elige el tipo de cesión que deseas realizar:
          </Typography>
          <FormControl fullWidth variant="outlined">
            <InputLabel id="cession-type-label">Tipo de cesión</InputLabel>
            <Select
              labelId="cession-type-label"
              value={cessionType}
              onChange={(e) => setCessionType(e.target.value as CessionType)}
              label="Tipo de cesión"
            >
              <MenuItem value={CessionType.REVERSE}>
                Revertir al emisor
              </MenuItem>
              <MenuItem value={CessionType.EXTERNAL}>
                Ceder a otra empresa
              </MenuItem>
            </Select>
          </FormControl>
          <Typography
            variant="body2"
            style={{ marginTop: '10px', color: 'text.secondary' }}
          >
            {cessionType === CessionType.REVERSE
              ? 'Las facturas serán revertidas al emisor original'
              : 'Las facturas serán cedidas a otra empresa'}
          </Typography>
        </div>
      ),
    },
    {
      label: 'Seleccionar empresa',
      description:
        cessionType === CessionType.EXTERNAL
          ? 'Selecciona la empresa a la cual ceder las facturas'
          : 'No es necesario seleccionar empresa para revertir',
      content: (
        <div>
          {cessionType === CessionType.EXTERNAL ? (
            <>
              <Typography variant="body1" style={{ marginBottom: '15px' }}>
                Selecciona la empresa a la cual ceder las facturas:
              </Typography>
              <RutSearcher
                setBusiness={setSelectedBusiness}
                setSearched={setBusinessSearched}
              />
              {selectedBusiness && (
                <div style={{ padding: '10px 0px' }}>
                  <Typography
                    variant="body1"
                    style={{ color: 'success.main', marginBottom: '10px' }}
                  >
                    ✓ Empresa seleccionada correctamente
                  </Typography>
                </div>
              )}
              {!selectedBusiness && businessSearched && (
                <div style={{ padding: '10px 0px' }}>
                  <Typography variant="body1" style={{ color: 'error.main' }}>
                    No encontramos una empresa con ese RUT. Por favor verifica
                    el RUT o selecciona otra empresa.
                  </Typography>
                </div>
              )}
            </>
          ) : (
            <Typography variant="body1" style={{ color: 'text.secondary' }}>
              Para revertir las facturas al emisor original, no es necesario
              seleccionar una empresa. Las facturas serán revertidas
              automáticamente.
            </Typography>
          )}
        </div>
      ),
      disabled: cessionType === CessionType.EXTERNAL && !selectedBusiness,
    },
  ];

  const handleStepChange = (step: number) => {
    setActiveStep(step);
  };

  const handleComplete = () => {
    setStepperCompleted(true);
  };

  const handleReset = () => {
    setActiveStep(0);
    setStepperCompleted(false);
    setCessionType(CessionType.REVERSE);
    setSelectedBusiness(null);
    setBusinessSearched(false);
    setSelectedCessions([]);
    setSelectedCessionsMap(new Map());
    setDocumentsWithStatus([]);
    setDocumentsResult([]);
    setInitialLoading(false);
  };

  useEffect(() => {
    if (open) {
      setInitialLoading(true);
      void fetchCessionsToCreateNominee({ page: 1, pageSize: PAGE_SIZE });
    }
  }, [open]);

  useEffect(() => {
    const newSelections = selectedCessions.filter(
      (id) => !selectedCessionsMap.has(id),
    );
    const removedSelections = Array.from(selectedCessionsMap.keys()).filter(
      (id) => !selectedCessions.includes(id),
    );

    if (newSelections.length > 0 || removedSelections.length > 0) {
      const newMap = new Map(selectedCessionsMap);

      newSelections.forEach((id) => {
        const doc = cessions.find((c: any) => c.id === id);
        if (doc) {
          newMap.set(id, doc);
        }
      });

      removedSelections.forEach((id) => newMap.delete(id));

      setSelectedCessionsMap(newMap);
    }
  }, [selectedCessions, cessions]);

  useEffect(() => {}, [documentsWithStatus]);

  return (
    <Dialog maxWidth="md" fullWidth open={open}>
      <DialogTitle>
        <Typography variant="h6">
          {cessionType === CessionType.REVERSE
            ? 'Reverso de cesiones'
            : 'Cesión de facturas'}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {initialLoading ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '40px 20px',
            }}
          >
            <CenteredCircularProgress type="dialog" />
            <Typography
              variant="body2"
              color="text.secondary"
              style={{ marginTop: '16px', textAlign: 'center' }}
            >
              Cargando cesiones disponibles...
            </Typography>
          </div>
        ) : loading && activeStep !== 0 ? (
          <CenteredCircularProgress type="dialog" />
        ) : (
          <CustomStepper
            steps={steps}
            activeStep={activeStep}
            onStepChange={handleStepChange}
            onComplete={handleComplete}
            onReset={handleReset}
            completionMessage="¡Facturas listas para ser cedidas!"
            maxWidth="100%"
            sx={{ mt: 2 }}
          />
        )}
      </DialogContent>
      {!loading && !initialLoading && (
        <DialogActions>
          <CustomButton onClick={() => setOpen(false)} color="secondary">
            Salir
          </CustomButton>
          <CustomButton
            disabled={!canMakeCession || !stepperCompleted}
            onClick={() => handleSubmit()}
            color="primary"
          >
            Confirmar
          </CustomButton>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default CessionDialog;
